import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api.js';

const EXAMPLE = `{
  "title": "Example: add a search bar to the dashboard",
  "one_line": "Adds an inline search bar above the dashboard track list with debounced filtering.",
  "manifest": {
    "files_create": ["src/client/components/SearchBar.tsx"],
    "files_modify": [
      { "path": "src/client/pages/Queue.tsx", "why": "render search bar and filter tracks" }
    ],
    "tables_create": []
  },
  "will_not_touch": ["server API", "spec schema"],
  "prereqs": [],
  "cc_prompt": "Add a SearchBar component that filters the queue list by title or one-line. Debounce 200ms. Keep styling consistent with existing cards.",
  "success_criteria": [
    "Search bar renders above the queue groups",
    "Typing filters visible tracks live",
    "Empty query restores full list"
  ],
  "rollback": {
    "git_tag": "before-search-bar",
    "side_effects": "None — UI-only change, no schema or API impact."
  },
  "risk_level": "low"
}`;

export default function NewSpec() {
  const [text, setText] = useState(EXAMPLE);
  const [errors, setErrors] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function submit() {
    setErrors(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setErrors([`JSON parse error: ${(e as Error).message}`]);
      return;
    }
    setBusy(true);
    try {
      const r = await api.createTrack(parsed);
      navigate(`/tracks/${r.track.id}`);
    } catch (e) {
      if (e instanceof ApiError) {
        const list = Array.isArray((e.details as { errors?: string[] })?.errors)
          ? (e.details as { errors: string[] }).errors
          : [e.message];
        setErrors(list);
      } else {
        setErrors([(e as Error).message]);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="group-title">Paste spec JSON</div>
      {errors && (
        <div className="errors">
          <strong>Spec rejected</strong>
          <ul>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      <textarea
        className="spec-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
      />
      <div className="actions">
        <button className="btn-conductor" onClick={submit} disabled={busy}>
          {busy ? 'Validating…' : 'Validate & create'}
        </button>
      </div>
    </div>
  );
}
